import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import EastIcon from '@mui/icons-material/East';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigationIcon from '@mui/icons-material/Navigation';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8050Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8050 Guide: Direct Deposit for Form 990-T and 990-PF"
          description="Learn how to file Form 8050 to request your IRS refund by direct deposit, with step-by-step guidance and key instructions for Form 990-T and Form 990-PF filers "
          keywords=""
          canonical="resources/tax-exempt/form-8050-instructions/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight">8050 </span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Direct Deposit of IRS Refunds for 990-T and 990-PF Filers
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f8050instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-8050-instructions/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8050 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 8050? Form 8050, Direct Deposit of Tax
                          Exempt or Government Entity Tax Refund,
                        </strong>{' '}
                        is used to request that the IRS{' '}
                        <strong style={{ fontWeight: 600 }}>
                          directly deposit an eligible tax refund{' '}
                        </strong>{' '}
                        into a U.S. bank or other eligible financial account.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Who Can File?
                        </strong>{' '}
                        A filer requesting a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          direct-deposit refund with an original
                        </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-pf/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-PF
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          990-T
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-1120-pol/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          1120-POL
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>or 4720</strong> may
                        file Form 8050.
                      </span>
                    </li>

                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 8050 provides the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          financial institution's routing number, account
                          number, account type, and filer information{' '}
                        </strong>{' '}
                        needed to process the direct deposit.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Supported Tax Returns:
                        </strong>{' '}
                        Form 8050 is attached to{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-PF, Form 990-T, Form 1120-POL, or Form
                          4720{' '}
                        </strong>{' '}
                        when requesting direct deposit of an eligible refund.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents{' '}
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 8050?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#needs" sx={{ color: '#0202ff' }}>
                          Who needs file Form 8050?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When do Form 990-T filers need Form 8050?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whendo" sx={{ color: '#0202ff' }}>
                          When do Form 990-PF filers need Form 8050?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#cannot" sx={{ color: '#0202ff' }}>
                          Who cannot request a Direct Deposit?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#long" sx={{ color: '#0202ff' }}>
                          How long does the IRS take to process refunds?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#happen" sx={{ color: '#0202ff' }}>
                          What happens if I enter an incorrect Routing or
                          Account Number?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to file Form 8050 with TaxZerone?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#add" sx={{ color: '#0202ff' }}>
                          Additional IRS References
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What is Form 8050?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 8050 is an IRS form that allows you to have your tax
                  refund deposited directly into your bank account, rather than
                  receiving a paper check.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This form is available to exempt organizations and private
                  foundations that file{' '}
                  <span style={{ fontWeight: 600 }}> Form 990-T</span> or{' '}
                  <span style={{ fontWeight: 600 }}> Form 990-PF.</span> It
                  offers a quicker and more secure way to get refunds, whether
                  they're going into a U.S. bank account or another eligible
                  account, such as a brokerage or mutual fund account.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The form can be used for refunds of any size, even those
                  exceeding $1 million, and while it's not mandatory, it's a
                  good idea to use it when you anticipate a refund.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="needs"
                >
                  Who needs to file Form 8050?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Form 8050 is intended for exempt organizations and private
                  foundations filing{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Form 990-T or Form 990-PF
                  </span>{' '}
                  that expect a tax refund. Organizations that prefer to receive
                  their refund by{' '}
                  <span style={{ fontWeight: 600 }}>
                    direct deposit into a U.S. bank or other eligible financial
                    account,
                  </span>{' '}
                  rather than waiting for a paper check, can use this form.
                  While optional, Form 8050 is recommended for a{' '}
                  <span style={{ fontWeight: 600 }}>
                    faster, more secure, and paperless
                  </span>{' '}
                  refund experience.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="when"
                >
                  When do Form 990-T filers need Form 8050?
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Organizations filing Form 990-T may be able to get a
                      refund if they paid more unrelated business income tax
                      (UBIT) than required, made extra estimated tax payments,
                      or qualify for refundable tax credits.{' '}
                    </Box>
                  </li>

                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Form 8050 provides a way for these organizations to get
                      their refunds via direct deposit, rather than waiting for
                      a paper check.{' '}
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      By including Form 8050 with their Form 990-T, the IRS can
                      electronically deposit the refund into the organization's
                      U.S. bank account or another qualifying financial
                      account.{' '}
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      This process accelerates payment, minimizes the chances of
                      checks getting lost or delayed, and streamlines cash-flow
                      management.{' '}
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Though it&apos;s not mandatory, many 990-T filers
                      anticipating a refund choose to use Form 8050.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whendo"
                >
                  When do Form 990-PF filers need Form 8050?
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Private foundations that submit Form 990-PF might be due a
                      refund if they&apos;ve overpaid excise taxes, submitted
                      too much in estimated tax payments, or are claiming
                      eligible credits.
                    </Box>
                  </li>

                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Form 8050 offers a way for these refunds to be directly
                      deposited into the foundation&apos;s U.S. bank account or
                      another qualifying financial institution, rather than
                      being sent as a paper check.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      By including Form 8050 with Form 990-PF, the IRS can
                      electronically deposit the refund.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      This helps foundations get their money sooner, minimizes
                      the chances of checks getting lost or delayed, and
                      simplifies financial management.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Though it's not mandatory, Form 8050 is commonly used when
                      a refund is anticipated.
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="cannot"
                >
                  Who cannot request a Direct Deposit?
                </Typography>
                <Typography variant="body1">
                  A filer cannot request a direct deposit in the following
                  situations:
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 2,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      If the refund is going to a{' '}
                      <span style={{ fontWeight: 600 }}> foreign bank</span> or
                      a{' '}
                      <span style={{ fontWeight: 600 }}>
                        foreign branch of a U.S. bank
                      </span>
                    </Box>
                  </li>

                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      If the filer has{' '}
                      <span style={{ fontWeight: 600 }}>
                        applied for an Employer Identification Number (EIN)
                      </span>{' '}
                      but is filing the return{' '}
                      <span style={{ fontWeight: 600 }}>
                        before the EIN is issued
                      </span>
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In these cases, the IRS will issue the refund as a{' '}
                  <span style={{ fontWeight: 600 }}>paper check</span> instead
                  of a direct deposit.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="long"
                >
                  How long does the IRS take to process refunds?
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 2,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Most IRS refunds are issued within{' '}
                      <span style={{ fontWeight: 600 }}>21 days</span> once the
                      return has been processed.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Once the IRS processes the return and approves the refund,
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        direct deposit refunds are issued faster than paper
                        checks.
                      </span>
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      The exact timing can vary depending on the return type and
                      whether the return needs additional review, but direct
                      deposit is generally the{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        quickest way to receive a refund.
                      </span>
                    </Box>
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="happen"
                >
                  What happens if I enter an incorrect Routing or Account
                  Number?
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      If the routing number or account number is incorrect, the
                      IRS{' '}
                      <span style={{ fontWeight: 600 }}>
                        cannot complete the direct deposit.
                      </span>
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      The financial institution may reject the deposit, and the
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        refund will be returned to the IRS.
                      </span>{' '}
                      In that case, the IRS will issue the refund as a{' '}
                      <span style={{ fontWeight: 600 }}>paper check,</span>{' '}
                      which can delay delivery.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <NavigationIcon
                      sx={{
                        rotate: '90deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      If incorrect banking details cause a refund to be
                      deposited into a third-party account, the IRS will not
                      step in to recover the funds, and it becomes the
                      taxpayer’s responsibility to resolve the issue.
                    </Box>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Because of this, it’s important to{' '}
                  <span style={{ fontWeight: 600 }}>
                    verify bank details carefully
                  </span>{' '}
                  before filing Form 8050.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="howto"
                >
                  How to File Form 8050 with TaxZerone?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  TaxZerone streamlines the process for exempt organizations and
                  private foundations, enabling them to file Form 8050 in
                  conjunction with Form 990-T or Form 990-PF. The platform
                  guides users through entering the required banking
                  information, making sure Form 8050 is properly connected to
                  the return. This connection allows for direct deposit of any
                  refund.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8050-img.webp" />
                  <source srcSet="/assets/images/f8050-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8050-img.png"
                    loading="lazy"
                    alt="form 8050 attach form of 990-pf, 990-t"
                    width={760}
                    height={271}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 1: Routing Number{' '}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Enter the financial institution’s{' '}
                      <span style={{ fontWeight: 600 }}> routing number</span>{' '}
                      in the space provided.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>Make sure your bank can accept direct deposits.</Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>You can find the routing number on your check. </Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      If your payment goes through a different bank than where
                      your account is, check with your bank to get the correct
                      routing number.{' '}
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      {' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Do not use a deposit slip
                      </span>{' '}
                      to verify the routing number.{' '}
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 2: Account Number
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      Input your account number precisely as it appears on your
                      check, reading from left to right.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>Do not fill in any additional boxes.</Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 17 },
                    fontWeight: 600,
                    mb: 1,
                    lineHeight: 1.4,
                  }}
                >
                  Line 3: Choose your Account Type (Checking or Savings)
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 3,

                    justifyContent: 'flex-start',
                    ml: -2,
                    '& li': {
                      listStyleType: 'none',
                      display: 'flex',
                      textAlign: 'left',
                      mb: 1.5,
                    },
                  }}
                >
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}> Checking:</span> Pick
                      this if you want your refund in a checking account. These
                      are usually for regular spending. Nonprofits and
                      government agencies often choose this.
                    </Box>
                  </li>
                  <li>
                    {' '}
                    <EastIcon
                      sx={{
                        rotate: '0deg',
                        mb: -0.7,
                        mr: 1,
                        color: '#1da94a',
                      }}
                    />{' '}
                    <Box>
                      <span style={{ fontWeight: 600 }}>Savings:</span> Select
                      this if you'd rather have your refund in a savings
                      account, where you keep your funds.{' '}
                    </Box>
                  </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 3,
                    backgroundColor: '#fde3e3',
                    borderRadius: 3,
                    p: 1,
                    borderLeft: '3px solid rgb(192, 38, 82)',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 0.2, fontWeight: 600, fontStyle: 'italic' }}
                  >
                    Important:
                  </Typography>
                  <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                    <li>Only check one box. </li>
                    <li>
                      Double-check that the account type matches the routing and
                      account numbers you provided in Lines 1 and 2.{' '}
                    </li>
                    <li>
                      Choosing the wrong account type could cause your refund to
                      be delayed or even rejected.{' '}
                    </li>
                  </Box>
                </Box>

                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderRadius: 2, scrollMarginTop: 88 }}
                  id="add"
                >
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        This content is based on official{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/f8050.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS Instructions Form 8050
                        </Link>{' '}
                        (Direct Deposit of Tax-Exempt or Government Entity Tax
                        Refund). For complete and updated information, refer to
                        the official IRS website.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to e-file your Form 990-T or 990-PF?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure compliance with IRS requirements for direct deposit of
              tax-exempt or government entity tax refunds.{' '}
              <Hidden smDown>
                <br />
              </Hidden>{' '}
              TaxZerone provides a secure and reliable way to attach IRS Form
              8050 and electronically file your Form 990-T or 990-PF accurately
              and with confidence.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=f8050instruction`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8050Instructions;
